import { useEffect, useRef } from "react";
import axios from "axios";

import { API_BASE as API } from "../lib/apiBase";
import { APP_VERSION } from "../version";

/**
 * B1053 — Canari de transport axios (Eric 2026-09-04 SPEC STRICK PROD).
 *
 * POURQUOI CE COMPOSANT EXISTE
 * ────────────────────────────
 * B1052 : `axios.defaults.adapter` est devenu un tableau dans axios 1.7, donc
 * notre wrapper de déduplication jetait un `TypeError` et **aucune requête
 * axios ne quittait le navigateur**. Le backend était parfaitement sain — donc
 * ni `/api/health`, ni UptimeRobot, ni la Sentinelle Connexion serveur ne
 * pouvaient voir quoi que ce soit. Pire : le diagnostic intégré de l'app
 * utilise `fetch()` natif, il affichait donc « Ping 200 / Santé 200 » pendant
 * que la connexion était morte. Deux jours de panne, zéro alerte.
 *
 * Seul le navigateur peut détecter cette classe de panne. Au boot, on émet le
 * MÊME `GET /api/canary` de deux façons :
 *
 *   • en **axios** (le transport réel de l'application)
 *   • en **`fetch()` natif** (le transport du navigateur, non instrumenté)
 *
 * Si `fetch` réussit et qu'axios échoue, le transport de l'app est cassé : on
 * remonte un rapport détaillé au backend — **obligatoirement en `fetch`**,
 * puisque axios est précisément ce qui ne marche pas — et une alerte courriel
 * part immédiatement.
 *
 * Contraintes de conception :
 *   • une seule exécution par chargement de page (`ranRef`) ;
 *   • `_noDedup` + `_maxRetries: 0` : on veut le verdict brut, pas 8 retries ;
 *   • aucun rendu, aucun impact visuel, aucune exception propagée ;
 *   • `/api/canary` est public → le canari fonctionne aussi sur la page de
 *     connexion, là où le problème se manifeste.
 */
const CANARY_PATH = "/api/canary";
const AXIOS_TIMEOUT_MS = 8000;

// Garde de portée MODULE (et non `useRef`) : en développement/preview,
// React.StrictMode monte-démonte-remonte les effets, ce qui déclenchait deux
// sondes par chargement de page (constaté à la revue iter211). Une seule sonde
// par chargement suffit et évite tout bruit inutile dans les logs.
let _canaryHasRun = false;

function describeAdapter() {
  try {
    const spec = axios.defaults.adapter;
    if (typeof spec === "function") return `function:${spec.name || "anonymous"}`;
    if (Array.isArray(spec)) return `array:[${spec.join(",")}]`;
    return `${typeof spec}:${String(spec)}`;
  } catch {
    return "unreadable";
  }
}

export const __runTransportCanary = async ({ axiosGet, fetchFn, report }) => {
  let axiosOk = false;
  let axiosError = "";
  let axiosCode = "";
  let axiosHasConfig = false;
  try {
    await axiosGet();
    axiosOk = true;
  } catch (err) {
    axiosError = String(err?.message || err || "unknown");
    axiosCode = String(err?.code || "");
    axiosHasConfig = !!err?.config;
  }

  // Axios fonctionne → rien à signaler. Cas de très loin le plus fréquent :
  // on sort sans même solliciter `fetch`.
  if (axiosOk) return { axiosOk: true, reported: false };

  let fetchOk = false;
  try {
    const res = await fetchFn();
    fetchOk = !!res && res.ok;
  } catch {
    fetchOk = false;
  }

  // Axios ET fetch échouent → c'est le réseau ou le backend, pas le transport
  // de l'app. Les sondes serveur couvrent déjà ce cas ; ne pas polluer.
  if (!fetchOk) return { axiosOk: false, fetchOk: false, reported: false };

  await report({
    axios_ok: false,
    fetch_ok: true,
    axios_error: axiosError.slice(0, 400),
    axios_code: axiosCode.slice(0, 80),
    axios_has_config: axiosHasConfig,
  });
  return { axiosOk: false, fetchOk: true, reported: true };
};

export default function AxiosTransportCanary() {
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current || _canaryHasRun) return;
    ranRef.current = true;
    _canaryHasRun = true;

    const url = `${API}${CANARY_PATH}`;
    (async () => {
      try {
        await __runTransportCanary({
          axiosGet: () =>
            axios.get(url, {
              timeout: AXIOS_TIMEOUT_MS,
              silent: true,
              _noDedup: true,
              _maxRetries: 0,
            }),
          fetchFn: () => fetch(url, { method: "GET", credentials: "omit", cache: "no-store" }),
          report: async (payload) => {
            // eslint-disable-next-line no-console
            console.error(
              "[canary] Le transport axios est cassé alors que fetch() fonctionne — "
              + "signalement au backend. Détail :",
              payload
            );
            const body = {
              ...payload,
              adapter_installed: typeof axios.defaults.adapter === "function",
              adapter_spec: describeAdapter(),
              bundle_version: APP_VERSION,
              page_url: (typeof window !== "undefined" ? window.location.href : "").slice(0, 300),
              user_agent: (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 300),
            };
            await fetch(`${API}/api/sentinel/transport-canary`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
              credentials: "omit",
              cache: "no-store",
            });
          },
        });
      } catch {
        /* le canari ne doit JAMAIS casser l'application */
      }
    })();
  }, []);

  return null;
}
